import type { CSSProperties, JSX } from 'react'
import { formatKw } from './Pill.js'
import { T } from '../tokens.js'

/**
 * An **indicative** kW figure — a strategy's engineering yield, not a metered
 * estimate (R-33, spec §7.6).
 *
 * ## Why this is not `KwFigure`, which is the first thing to check in review
 *
 * `Pill.tsx` says of `KwFigure`: *"`confidence` is required and there is no
 * variant that drops the label: `label="pill"` draws one, `label="adjacent"`
 * points at one. Rendering a bare kW figure in this SPA means not using this
 * component, which is a thing a reviewer can see in a diff."* That tripwire is
 * doing its job here, and this is the answer to it rather than a way round it.
 *
 * Display rule 1 governs **estimates**: a figure derived from the customer's
 * own interval data, which carries a confidence label because the pipeline
 * computed one for it. An indicative yield is not an estimate. It is
 * `endUseKw × yieldFraction` over a library the program manager wrote — there
 * is no confidence computation anywhere in `strategies.ts` and no field on
 * `RankedStrategy` to read one from. Passing `confidence="low"` to `KwFigure`
 * to satisfy the type would be inventing a label the system never produced and
 * putting it on a screen in front of a customer, which is a worse failure than
 * the one the required prop exists to prevent.
 *
 * So this is a **sibling** of `KwFigure`, not a variant of it, and it is
 * deliberately unmistakable for one:
 *
 * | | metered nomination | indicative yield |
 * |---|---|---|
 * | size | 44px (`KW_SIZES.kpi`) | 21px, or 28px on the aggregate |
 * | weight | 800 (`display`) | 600 (`semi`) |
 * | colour | `navy` | `body` |
 * | ground | the blue KPI tint | white, or `rescore` on the aggregate |
 * | label | a confidence pill | the word `indicative` |
 *
 * **The two sizes are both this component's, and neither is 44px.** The
 * strategy surface's aggregate is a section summary rather than another row, so
 * it is drawn one step up — `AGGREGATE_KW_SIZE` below. Weight, colour and label
 * do not move with it: four of the five rows of that table still separate it
 * from the metered figure, and the fifth stays 16px short.
 *
 * It never sets `data-kw-size`, which is how a test tells the two apart without
 * asking jsdom for a computed style it does not have.
 *
 * ## The word, not a tooltip
 *
 * R-33: *"Indicative yields are labeled indicative wherever they appear."* The
 * word is rendered text next to the figure — in the accessibility tree, in
 * `innerText`, in find-in-page and on a printout. A `title` attribute is none
 * of those things to the person the laptop is turned towards.
 */

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

/**
 * The figure's own size in px. A named constant rather than a literal so that
 * "this is not the 44px metered figure" is a comparison a reader can make
 * against `KW_SIZES.kpi` in `Pill.tsx` without measuring anything.
 */
export const INDICATIVE_KW_SIZE = 21

/**
 * The aggregate's size in px — one step up from a row's, and still 16px short
 * of `KW_SIZES.kpi`.
 *
 * 28 rather than 32 or 36 because the gap to 44 has to survive being read at a
 * glance on a laptop the customer is looking at: 28 next to 44 is plainly the
 * smaller of two figures, 36 next to 44 is two figures the same size. It is
 * also a real step up from the 21px rows, which is the whole point of the
 * change — a summary that renders at row size reads as one more row.
 */
export const AGGREGATE_KW_SIZE = 28

export interface IndicativeKwProps {
  kw: number
  /**
   * Defaulted rather than required: every call site in this package wants the
   * same name, and a testid that varies per row is one a test cannot collect
   * with `getAllByTestId` — which is exactly how the "never presents a plain
   * sum" assertion reaches every figure on the surface.
   */
  testId?: string
  /**
   * The figure's px size. Defaulted to `INDICATIVE_KW_SIZE`; the only caller
   * that passes anything else is the strategy surface's aggregate.
   *
   * **It is capped here rather than trusted to call sites.** A prop that can be
   * set to 44 is a prop by which this component becomes the metered nomination,
   * and the whole file exists to make that impossible. `Math.min` is one line
   * against a class of change no reviewer would spot in a call site.
   */
  size?: number
}

/**
 * The largest a yield may be drawn, whatever a caller asks for. Deliberately
 * below `KW_SIZES.kpi` (44) with room to spare rather than at it.
 */
export const INDICATIVE_KW_MAX_SIZE = 32

export function IndicativeKw(
  { kw, testId = 'indicative-kw', size = INDICATIVE_KW_SIZE }: IndicativeKwProps,
): JSX.Element {
  return (
    <span
      data-testid={testId}
      // The machine-readable figure, for the same reason `KwFigure` carries
      // one: a test that re-parsed the formatted string would be asserting
      // against `formatKw`, and a test that read a computed colour would be
      // asserting against jsdom.
      data-kw={kw}
      data-indicative="true"
      // `fontSize` is set HERE, on the element the tests already hold, and the
      // figure below inherits it. Setting it on the inner span and mirroring it
      // in an attribute is two expressions that can disagree — the exact defect
      // `FRAMING_SIZE` in `StrategyList.tsx` documents, where a test read the
      // attribute while the rendering dropped to 10px. There is one expression
      // and `element.style.fontSize` is it.
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 8,
        whiteSpace: 'nowrap',
        fontSize: Math.min(size, INDICATIVE_KW_MAX_SIZE),
      }}
    >
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: T.weight.semi,
          color: T.color.body,
        }}
      >
        {formatKw(kw)}
      </span>
      <span style={microLabel}>indicative</span>
    </span>
  )
}
